import React, { useState, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { Select } from '../components/ui/Select';
import { Dialog } from '../components/ui/Dialog';
import { Panel } from '../components/ui/Panel';
import { EmptyState } from '../components/ui/EmptyState';
import { CATEGORY } from '../components/ui/categoryColors';
import { DataTable } from '../components/table/DataTable';

// Fix Leaflet default icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

/**
 * LeafletFlightMap - Interactive map showing flight path
 */
const LeafletFlightMap = ({ frames, currentIdx, allHeaders }) => {
    const latKey = allHeaders.find(h => h.toLowerCase().includes('lat')) || 'lat';
    const lonKey = allHeaders.find(h => h.toLowerCase().includes('lon')) || 'lon';

    // Get GPS points
    const gpsPoints = frames
        .map((f, i) => ({ i, lat: f[latKey], lon: f[lonKey] }))
        .filter(p => p.lat != null && p.lon != null && !isNaN(p.lat) && !isNaN(p.lon));

    if (gpsPoints.length === 0) {
        return (
            <div className="w-full h-[200px] flex items-center justify-center text-on-surface-variant text-sm">
                No GPS data available
            </div>
        );
    }

    // Calculate center and bounds
    const lats = gpsPoints.map(p => p.lat);
    const lons = gpsPoints.map(p => p.lon);
    const center = [
        (Math.min(...lats) + Math.max(...lats)) / 2,
        (Math.min(...lons) + Math.max(...lons)) / 2
    ];

    // Path coordinates for Polyline
    const pathCoords = gpsPoints.map(p => [p.lat, p.lon]);

    // Current position
    const currentFrame = frames[currentIdx];
    const currentPos = currentFrame && currentFrame[latKey] != null && currentFrame[lonKey] != null
        ? [currentFrame[latKey], currentFrame[lonKey]]
        : null;

    return (
        <div style={{ height: '200px', width: '100%' }}>
            <MapContainer
                center={center}
                zoom={16}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
            >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                {/* Flight path */}
                <Polyline
                    positions={pathCoords.slice(0, gpsPoints.findIndex(p => p.i === currentIdx) + 1)}
                    color={CATEGORY.blue.ink.hex}
                    weight={3}
                />
                {/* Current position marker */}
                {currentPos && (
                    <Marker position={currentPos}>
                        <Popup>Current Position</Popup>
                    </Marker>
                )}
            </MapContainer>
        </div>
    );
};

const formatTime = (timestamp) => {
    if (!timestamp) return '--:--:--';
    try {
        const d = new Date(timestamp);
        return d.toLocaleTimeString();
    } catch {
        return '--:--:--';
    }
};

const headerLabel = (h) => h.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const FRAME_ROW_ID = () => 'frame';

/**
 * LogViewer - Displays flight log data from CSV
 *
 * Props:
 * - csvData: string - Raw CSV data
 * - droneSerial: string - Drone serial number
 * - flightDate: string - Flight date
 * - flightReviewUrl: string - URL to full analysis in Flight Review
 * - onClose: function - Callback to close viewer
 * - onDelete: function - Callback to delete log entry (optional)
 */
const LogViewer = ({ csvData, droneSerial, flightDate, flightReviewUrl, onClose, onDelete }) => {
    const [frames, setFrames] = useState([]);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [selectedField, setSelectedField] = useState("alt_gps");
    const [allHeaders, setAllHeaders] = useState([]);
    const [useLeafletMap, setUseLeafletMap] = useState(true);
    // State, not refs: Dialog mounts its children a render or two late, so the effect must re-run when a canvas attaches.
    const [chartEl, setChartEl] = useState(null);
    const [mapEl, setMapEl] = useState(null);

    // Parse CSV data on mount
    useEffect(() => {
        if (csvData) {
            const parsed = parseCsv(csvData);
            setFrames(parsed);
            if (parsed.length > 0) setCurrentIdx(0);
        }
    }, [csvData]);

    // Set default field when headers are loaded
    useEffect(() => {
        if (allHeaders.length > 0 && !allHeaders.includes(selectedField)) {
            // Try to find altitude field, otherwise use first non-timestamp field
            const altField = allHeaders.find(h => h.toLowerCase().includes('alt'));
            const defaultField = altField || allHeaders.find(h => h !== 'timestamp') || allHeaders[0];
            setSelectedField(defaultField);
        }
    }, [allHeaders]);

    // Draw chart when frames or selection changes
    useEffect(() => {
        if (frames.length > 0) {
            drawChart();
            drawMap();
        }
    }, [frames, currentIdx, selectedField, chartEl, mapEl]);

    const parseCsv = (text) => {
        const lines = text.split(/\r?\n/);
        const result = [];
        let headers = null;

        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            if (!headers) {
                headers = trimmed.split(',').map(h => h.trim());
                setAllHeaders(headers);
                continue;
            }

            const values = trimmed.split(',').map(v => v.trim());
            if (values.length !== headers.length) continue;

            const row = {};
            headers.forEach((h, idx) => {
                // Try to parse as number, fallback to string
                const val = values[idx];
                const num = parseFloat(val);
                row[h] = (!isNaN(num) && val !== '') ? num : val;
            });

            result.push(row);
        }

        return result;
    };

    const drawChart = () => {
        const canvas = chartEl;
        if (!canvas || frames.length === 0) return;

        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        // offsetWidth ignores the Dialog's scale-in transform, unlike getBoundingClientRect
        const rect = { width: canvas.offsetWidth, height: canvas.offsetHeight };

        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);

        const W = rect.width;
        const H = rect.height;
        const P = { l: 50, r: 10, t: 10, b: 25 };
        const plotW = W - P.l - P.r;
        const plotH = H - P.t - P.b;

        // Clear and background
        ctx.fillStyle = '#101010';
        ctx.fillRect(0, 0, W, H);

        // Collect data points
        const data = frames
            .map((f, i) => ({ i, v: f[selectedField], t: f.timestamp }))
            .filter(d => d.v != null && !isNaN(d.v));

        if (data.length === 0) {
            ctx.fillStyle = '#666';
            ctx.font = '14px system-ui';
            ctx.fillText('No data for selected field', P.l + 10, H / 2);
            return;
        }

        // Calculate extents
        const values = data.map(d => d.v);
        const vMin = Math.min(...values) - 1;
        const vMax = Math.max(...values) + 1;

        // Draw grid
        ctx.strokeStyle = '#2a2a2a';
        ctx.lineWidth = 1;
        for (let i = 0; i <= 4; i++) {
            const y = P.t + (plotH * i / 4);
            ctx.beginPath();
            ctx.moveTo(P.l, y);
            ctx.lineTo(P.l + plotW, y);
            ctx.stroke();

            const val = vMax - ((vMax - vMin) * i / 4);
            ctx.fillStyle = '#888';
            ctx.font = '10px monospace';
            ctx.fillText(val.toFixed(1), 5, y + 4);
        }

        // Draw line
        ctx.strokeStyle = CATEGORY.blue.ink.hex;
        ctx.lineWidth = 2;
        ctx.beginPath();

        data.forEach((d, i) => {
            const x = P.l + (d.i / (frames.length - 1)) * plotW;
            const y = P.t + ((vMax - d.v) / (vMax - vMin)) * plotH;

            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
        });
        ctx.stroke();

        // Draw current position marker
        const currentFrame = frames[currentIdx];
        if (currentFrame && currentFrame[selectedField] != null) {
            const x = P.l + (currentIdx / (frames.length - 1)) * plotW;
            const y = P.t + ((vMax - currentFrame[selectedField]) / (vMax - vMin)) * plotH;

            ctx.fillStyle = CATEGORY.yellow.ink.hex;
            ctx.beginPath();
            ctx.arc(x, y, 6, 0, Math.PI * 2);
            ctx.fill();

            // Vertical line
            ctx.strokeStyle = CATEGORY.yellow.ink.hex;
            ctx.setLineDash([4, 4]);
            ctx.beginPath();
            ctx.moveTo(x, P.t);
            ctx.lineTo(x, P.t + plotH);
            ctx.stroke();
            ctx.setLineDash([]);
        }

        // Draw border
        ctx.strokeStyle = '#333';
        ctx.strokeRect(P.l, P.t, plotW, plotH);
    };

    const drawMap = () => {
        const canvas = mapEl;
        if (!canvas || frames.length === 0) return;

        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        // offsetWidth ignores the Dialog's scale-in transform, unlike getBoundingClientRect
        const rect = { width: canvas.offsetWidth, height: canvas.offsetHeight };

        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);

        const W = rect.width;
        const H = rect.height;
        const P = { l: 10, r: 10, t: 10, b: 10 };
        const plotW = W - P.l - P.r;
        const plotH = H - P.t - P.b;

        // Clear and background
        ctx.fillStyle = '#0f0f0f';
        ctx.fillRect(0, 0, W, H);

        // Collect lat/lon points - check common variations
        const latKey = allHeaders.find(h => h.toLowerCase().includes('lat')) || 'lat';
        const lonKey = allHeaders.find(h => h.toLowerCase().includes('lon')) || 'lon';

        const points = frames
            .map((f, i) => ({ i, lat: f[latKey], lon: f[lonKey] }))
            .filter(p => p.lat != null && p.lon != null && !isNaN(p.lat) && !isNaN(p.lon));

        if (points.length === 0) {
            ctx.fillStyle = '#666';
            ctx.font = '14px system-ui';
            ctx.fillText('No GPS data', P.l + 10, H / 2);
            return;
        }

        // Calculate extents
        const lats = points.map(p => p.lat);
        const lons = points.map(p => p.lon);
        const latMin = Math.min(...lats);
        const latMax = Math.max(...lats);
        const lonMin = Math.min(...lons);
        const lonMax = Math.max(...lons);

        // Add padding
        const latPad = Math.max((latMax - latMin) * 0.1, 0.0001);
        const lonPad = Math.max((lonMax - lonMin) * 0.1, 0.0001);

        const toX = (lon) => P.l + ((lon - (lonMin - lonPad)) / ((lonMax + lonPad) - (lonMin - lonPad))) * plotW;
        const toY = (lat) => P.t + plotH - ((lat - (latMin - latPad)) / ((latMax + latPad) - (latMin - latPad))) * plotH;

        // Draw grid
        ctx.strokeStyle = '#1a1a1a';
        for (let i = 0; i <= 4; i++) {
            const y = P.t + (plotH * i / 4);
            const x = P.l + (plotW * i / 4);
            ctx.beginPath();
            ctx.moveTo(P.l, y);
            ctx.lineTo(P.l + plotW, y);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(x, P.t);
            ctx.lineTo(x, P.t + plotH);
            ctx.stroke();
        }

        // Draw path up to current index
        ctx.strokeStyle = CATEGORY.blue.ink.hex;
        ctx.lineWidth = 2;
        ctx.beginPath();

        const trailPoints = points.filter(p => p.i <= currentIdx);
        trailPoints.forEach((p, i) => {
            const x = toX(p.lon);
            const y = toY(p.lat);
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
        });
        ctx.stroke();

        // Draw current position
        const currentFrame = frames[currentIdx];
        if (currentFrame && currentFrame[latKey] != null && currentFrame[lonKey] != null) {
            const x = toX(currentFrame[lonKey]);
            const y = toY(currentFrame[latKey]);

            ctx.fillStyle = CATEGORY.yellow.ink.hex;
            ctx.beginPath();
            ctx.arc(x, y, 6, 0, Math.PI * 2);
            ctx.fill();
        }

        // Draw border
        ctx.strokeStyle = '#333';
        ctx.strokeRect(P.l, P.t, plotW, plotH);
    };

    const currentFrame = frames[currentIdx] || {};

    // Generate fields list from all headers, exclude timestamp as it's shown separately
    const fields = allHeaders
        .filter(h => h !== 'timestamp')
        .map(h => ({ key: h, label: headerLabel(h) }));

    const dataColumns = useMemo(() => allHeaders.map((h) => ({
        id: h,
        header: headerLabel(h),
        meta: { cellClass: 'font-mono whitespace-nowrap' },
        cell: ({ row }) => {
            const val = row.original[h];
            if (val == null || val === '') return '-';
            if (h === 'timestamp') return formatTime(val);
            // Format numbers: more decimals for small values, fewer for large
            if (typeof val === 'number') return Math.abs(val) < 10 ? val.toFixed(6) : val.toFixed(2);
            return val;
        },
    })), [allHeaders]);

    return (
        <Dialog
            open
            onClose={onClose}
            size="2xl"
            title="Flight Log Viewer"
            meta={<>{droneSerial} {flightDate && `• ${new Date(flightDate).toLocaleDateString()}`}</>}
            footer={(flightReviewUrl || onDelete) ? (
                <>
                    {flightReviewUrl && (
                        <Tooltip label="Open full analysis in Flight Review">
                            <a
                                href={flightReviewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${buttonClass('tonal', 'md')} gap-1`}
                            >
                                Advanced Analysis
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                            </a>
                        </Tooltip>
                    )}
                    {onDelete && (
                        <Tooltip label="Delete log entry">
                            <button
                                onClick={onDelete}
                                className={buttonClass('danger', 'md')}
                            >
                                Delete
                            </button>
                        </Tooltip>
                    )}
                </>
            ) : undefined}
        >
            {frames.length === 0 ? (
                <EmptyState variant="area">No data available</EmptyState>
            ) : (
                <>
                    {/* Current Time & Stats */}
                    <div className="flex items-center gap-4 mb-4">
                        <div className="text-3xl font-mono text-white">
                            {formatTime(currentFrame.timestamp)}
                        </div>
                        <div className="text-sm text-on-surface-variant">
                            Frame {currentIdx + 1} / {frames.length}
                        </div>
                        <div className="flex-1" />
                        <Select
                            label="Chart field"
                            size="sm"
                            width="fit"
                            value={selectedField}
                            onChange={setSelectedField}
                            options={fields.map(f => ({ key: f.key, label: f.label }))}
                        />
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {/* Time Series Chart */}
                        <Panel on="panel" padding="sm" title={`${fields.find(f => f.key === selectedField)?.label} over Time`} level={3}>
                            <canvas
                                ref={setChartEl}
                                className="w-full"
                                style={{ height: '200px' }}
                            />
                        </Panel>

                        {/* Map */}
                        <Panel
                            on="panel"
                            padding="sm"
                            title="Flight Path"
                            level={3}
                            actions={(
                                <button
                                    onClick={() => setUseLeafletMap(!useLeafletMap)}
                                    className="text-sm text-primary hover:underline"
                                >
                                    {useLeafletMap ? 'Canvas' : 'Map'}
                                </button>
                            )}
                        >
                            {useLeafletMap ? (
                                <LeafletFlightMap frames={frames} currentIdx={currentIdx} allHeaders={allHeaders} />
                            ) : (
                                <canvas
                                    ref={setMapEl}
                                    className="w-full"
                                    style={{ height: '200px' }}
                                />
                            )}
                        </Panel>
                    </div>

                    {/* Scrubber */}
                    <Panel on="panel" padding="sm" label="Frame scrubber" className="mb-4">
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-on-surface-variant font-mono">
                                {formatTime(frames[0]?.timestamp)}
                            </span>
                            <input
                                type="range"
                                min={0}
                                max={frames.length - 1}
                                value={currentIdx}
                                onChange={(e) => setCurrentIdx(Number(e.target.value))}
                                className="flex-1"
                            />
                            <span className="text-xs text-on-surface-variant font-mono">
                                {formatTime(frames[frames.length - 1]?.timestamp)}
                            </span>
                        </div>
                    </Panel>

                    {/* Data Table: the current frame, one row */}
                    <DataTable
                        data={[currentFrame]}
                        columns={dataColumns}
                        getRowId={FRAME_ROW_ID}
                        on="panel"
                        density="compact"
                        empty={null}
                    />
                </>
            )}
        </Dialog>
    );
};

export default LogViewer;
